import { z } from 'zod'

import { ConceptSchema } from '~/@entities/Concept'
import {
  AllGradeSubjectSchema,
  type GradeElementaryMiddle,
  type GradeHigh,
  GradeSchema,
  HighSchoolSubjectSchema,
  type Revision,
  RevisionSchema,
  type SchoolType,
  SchoolTypeSchema,
  type Semester,
  SemesterSchema,
} from '~/@entities/SchoolSystem'

/***************** 커리큘럼 노드 종류 *****************/
export const CURRICULUM_TYPE = [
  'MATH',
  'REVISION',
  'SCHOOL',
  'GRADE',
  'SEMESTER',
  'SUBJECT',
  'BIG_CHAPTER',
  'MIDDLE_CHAPTER',
  'LITTLE_CHAPTER',
  'CONCEPT',
  'TOPIC',
  'SUB_TOPIC',
  'CONCEPT_CHIP',
] as const

/***************** 서버 게시 상태 *****************/
export const CURRICULUM_STATUS = ['DRAFT', 'ACTIVE', 'HIDDEN', 'INACTIVE'] as const

/***************** 화면 노출 상태 *****************/
// 서버 게시 상태에 사전 오픈 키·비표준 고등 과목을 겹쳐 정한다. NOT_READY(지원 예정)는 서버가 주지 않고
// 화면이 콘텐츠 준비 여부로 덮어쓴다.
export const CURRICULUM_OPEN_STATUS = ['ACTIVE', 'HIDDEN', 'PRE_OPEN', 'NOT_READY'] as const

export const CurriculumTypeSchema = z.enum(CURRICULUM_TYPE)
export type CurriculumType = z.infer<typeof CurriculumTypeSchema>

export const CurriculumStatusSchema = z.enum(CURRICULUM_STATUS)
export type CurriculumStatus = z.infer<typeof CurriculumStatusSchema>

export const CurriculumOpenStatusSchema = z.enum(CURRICULUM_OPEN_STATUS)
export type CurriculumOpenStatus = z.infer<typeof CurriculumOpenStatusSchema>

/***************** 트라이 키 *****************/
// 루트부터 자기까지의 커리큘럼 id 를 '.' 로 이은 경로. 예) '1.2.5.12.50'
export const TrieKeySchema = z.string()
export type TrieKey = z.infer<typeof TrieKeySchema>

/***************** 커리큘럼 노드 *****************/
export const CurriculumSchema = z.object({
  key: TrieKeySchema,
  type: CurriculumTypeSchema,
  id: z.number(),
  name: z.string(),
  url: z.string().nullable(),
  hidden: z.boolean(),
  sequence: z.number(),
  revisionId: z.number().nullable(),
  revisionName: z.string().nullable(),
  schoolId: z.number().nullable(),
  schoolName: z.string().nullable(),
  gradeId: z.number().nullable(),
  gradeName: z.string().nullable(),
  semesterId: z.number().nullable(),
  semesterName: z.string().nullable(),
  subjectId: z.number().nullable(),
  subjectName: z.string().nullable(),
  bigChapterId: z.number().nullable(),
  bigChapterName: z.string().nullable(),
  middleChapterId: z.number().nullable(),
  middleChapterName: z.string().nullable(),
  littleChapterId: z.number().nullable(),
  littleChapterName: z.string().nullable(),
})
export type Curriculum = z.infer<typeof CurriculumSchema>

/***************** 학년·학기·과목 축(/curriculums/core) *****************/
const _coreNodeShape = {
  id: z.number(),
  trieKey: TrieKeySchema,
  revision: RevisionSchema,
  status: CurriculumStatusSchema,
}
const _ElementaryMiddleSchema = z.enum(['ELEMENTARY', 'MIDDLE'])

// 서버는 판별 필드가 전부 optional 인 클래스 하나(CoreCurriculum)를 다섯 배열에 쓴다. 배열마다 실제로 채우는
// 필드가 정해져 있어 배열별 스키마로 좁힌다.
export const CurriculumCoreNodesSchema = z.object({
  revisions: z.array(z.object({ ..._coreNodeShape, type: z.literal('REVISION') })),
  schoolTypes: z.array(
    z.object({ ..._coreNodeShape, type: z.literal('SCHOOL'), schoolType: SchoolTypeSchema }),
  ),
  grades: z.array(
    z.object({
      ..._coreNodeShape,
      type: z.literal('GRADE'),
      schoolType: _ElementaryMiddleSchema,
      grade: GradeSchema,
    }),
  ),
  semesters: z.array(
    z.object({
      ..._coreNodeShape,
      type: z.literal('SEMESTER'),
      schoolType: _ElementaryMiddleSchema,
      grade: GradeSchema,
      semester: SemesterSchema.unwrap(),
      orderingNumber: z.number(),
    }),
  ),
  subjects: z.array(
    z.object({
      ..._coreNodeShape,
      type: z.literal('SUBJECT'),
      schoolType: z.literal('HIGH'),
      subject: HighSchoolSubjectSchema,
      orderingNumber: z.number(),
    }),
  ),
})
export type CurriculumCoreNodes = z.infer<typeof CurriculumCoreNodesSchema>

/** 학년(초·중)·학기(초·중)·과목(고) 하나. `/curriculums/core` 응답을 adapter 가 조립한 값이다 */
export type CurriculumCoreItem<T extends SchoolType = SchoolType> = {
  isAllType: false
  id: Curriculum['id']
  trieKey: TrieKey
  /** 상위 trieKey */
  upperTrieKey: TrieKey
  /** 하위 trieKey 들(학기·과목이면 자기 자신). 학년은 그 아래 학기들 */
  lowerTrieKeys: TrieKey[]
  revision: Revision
  schoolType: T
  gradeSubject: GradeElementaryMiddle | GradeHigh
  semester: Semester
  orderingNumber?: number
  status: CurriculumOpenStatus
  /** ex) 초1(개정), 초1-1(개정), 고등수학(상)(개정) */
  label: string
  /** ex) 초1(개정), 초1-1(개정), 수학(상)(개정) */
  labelShort: string
  /** ex) 1 - 1(개정), 1(개정), 수학(상)(개정) */
  labelWithoutSchoolType: string
  /** ex) 초 1 - 1(개정), 1(개정) 고등수학(상)(개정) */
  labelWithSpace: string
  /** 고등은 학년이 없지만 과목을 학년으로 분류한 값 */
  categorizedGrade: GradeElementaryMiddle
}

/** 학교급 하나와 그 학교급이 걸린 개정들 */
export type CurriculumCoreSchool<T extends SchoolType = SchoolType> = {
  value: T
  label: string
  labelFull: string
  revisions: Revision[]
  trieKeys: TrieKey[]
  status: CurriculumOpenStatus
}

export type CurriculumCore = Pick<CurriculumCoreNodes, 'revisions' | 'schoolTypes'> & {
  schools: CurriculumCoreSchool[]
  grades: CurriculumCoreItem<'ELEMENTARY' | 'MIDDLE'>[]
  semesters: CurriculumCoreItem<'ELEMENTARY' | 'MIDDLE'>[]
  subjects: CurriculumCoreItem<'HIGH'>[]
}

/***************** 대·중·소단원 트리 *****************/
const _ChapterConceptSchema = ConceptSchema.pick({ id: true, name: true }).extend({
  priority: z.number(),
  // by-key 응답만 싣는다. 학교별 기출 응답에는 없다.
  topicCount: z.number().optional(),
  subTopicCount: z.number().optional(),
})
export type CurriculumChapterConcept = z.infer<typeof _ChapterConceptSchema>

const _LittleChapterSchema = z.object({
  id: z.number(),
  name: z.string(),
  concepts: z.array(_ChapterConceptSchema),
})
export type CurriculumLittleChapter = z.infer<typeof _LittleChapterSchema>

const _MiddleChapterSchema = z.object({
  id: z.number(),
  name: z.string(),
  littleChapters: z.array(_LittleChapterSchema),
})
export type CurriculumMiddleChapter = z.infer<typeof _MiddleChapterSchema>

const _BigChapterSchema = z.object({
  id: z.number(),
  name: z.string(),
  middleChapters: z.array(_MiddleChapterSchema),
})
export type CurriculumBigChapter = z.infer<typeof _BigChapterSchema>

/** 학기(초·중) 또는 과목(고) 하나 아래의 단원 트리. id 는 그 학기·과목의 커리큘럼 id 다 */
export const CurriculumChapterTreeSchema = z.object({
  id: z.number(),
  schoolType: SchoolTypeSchema,
  grade: AllGradeSubjectSchema,
  semester: SemesterSchema,
  revision: RevisionSchema,
  bigChapters: z.array(_BigChapterSchema),
})
export type CurriculumChapterTree = z.infer<typeof CurriculumChapterTreeSchema>

/** 단원 트리와 그 안의 유형 id 전부(by-key 응답) */
export const CurriculumConceptChapterTreeSchema = CurriculumChapterTreeSchema.extend({
  conceptIds: z.array(z.number()),
})
export type CurriculumConceptChapterTree = z.infer<typeof CurriculumConceptChapterTreeSchema>

/***************** 대단원 이름으로 찾은 중단원 *****************/
export const CurriculumChapterSearchSchema = z.object({
  chapters: z.array(
    z.object({
      semesterSubjectTrieKey: TrieKeySchema,
      schoolType: SchoolTypeSchema,
      revision: RevisionSchema,
      grade: AllGradeSubjectSchema,
      semester: SemesterSchema,
      bigChapterName: z.string(),
      middleChapterId: z.number(),
      middleChapterName: z.string(),
    }),
  ),
})
export type CurriculumChapterSearch = z.infer<typeof CurriculumChapterSearchSchema>

/***************** 깊이별로 펼친 교육과정 트리 *****************/
// 2026-10-14 백엔드 develop(6a0f8e7bb) 의 curriculum/controller/response/CurriculumTreeNodeResponse.kt·
// model/dto/CurriculumTreeNodeDto.kt 로 확인.
const _TreeNodeConceptSchema = ConceptSchema.pick({
  id: true,
  name: true,
  outOfCurriculumType: true,
}).extend({
  /** 유형 이론 이미지 주소. 이론 이미지가 없는 유형은 null 이다 */
  url: z.string().nullable(),
  priority: z.number(),
  topicCount: z.number(),
  subTopicCount: z.number(),
})
export type CurriculumConceptTreeNodeConcept = z.infer<typeof _TreeNodeConceptSchema>

/**
 * 교육과정 노드와 그 아래 노드·유형. 펼친 깊이의 노드 이름은 서버가 부모 이름을 이어 붙인 것(`초등학교 1학년 1학기`)이다.
 * trieKey 는 서버가 null 이면 키를 빼고(JsonInclude NON_NULL) 보낸다. included*IdList 는 자기와 모든 하위의 id 다.
 */
// 재귀 스키마는 선언 파일로 내보낼 때 z.infer 가 하위 노드를 Record 로 뭉개므로 타입을 먼저 적고 스키마를 거기에 맞춘다.
export type CurriculumConceptTreeNode = {
  id: number
  name: string
  trieKey?: TrieKey
  url: string | null
  hidden: boolean
  relationList: CurriculumConceptTreeNode[]
  includedRelationIdList: number[]
  conceptList: CurriculumConceptTreeNodeConcept[]
  includedConceptIdList: CurriculumConceptTreeNodeConcept['id'][]
}
export const CurriculumConceptTreeNodeSchema: z.ZodType<CurriculumConceptTreeNode> = z.object({
  id: z.number(),
  name: z.string(),
  trieKey: TrieKeySchema.optional(),
  url: z.string().nullable(),
  hidden: z.boolean(),
  relationList: z.lazy(() => z.array(CurriculumConceptTreeNodeSchema)),
  includedRelationIdList: z.array(z.number()),
  conceptList: z.array(_TreeNodeConceptSchema),
  includedConceptIdList: z.array(ConceptSchema.shape.id),
})

/***************** 출판사 교과서 단원 순서 *****************/
// 출판사 이름은 서버 enum 이 아니라 DB 에 적힌 한글 문자열이라 좁히지 않는다.
// 2026-10-14 백엔드 develop(6a0f8e7bb) 의 curriculum/controller/ProblemCurriculumDifferenceController.kt·
// response/PublisherAndCurriculumResponse.kt·ProblemCurriculumDifferenceResponse.kt 로 확인.

/** 출판사 하나와 그 출판사 교과서가 있는 학기(초·중)·과목(고). 고등은 학기가 null 이다 */
export const PublisherCurriculumSchema = z.object({
  publisher: z.string(),
  curriculums: z.array(
    z.object({
      trieKey: TrieKeySchema,
      schoolType: SchoolTypeSchema,
      grade: AllGradeSubjectSchema,
      semester: SemesterSchema,
    }),
  ),
})
export type PublisherCurriculum = z.infer<typeof PublisherCurriculumSchema>

/** 출판사 교과서와 매쓰플랫의 대단원 순서가 다른 곳. 설명이 없는 대단원은 description 이 null 이다 */
export const PublisherCurriculumDifferenceSchema = z.object({
  publisher: PublisherCurriculumSchema.shape.publisher,
  mathflatTrieKey: TrieKeySchema,
  differences: z.array(
    z.object({
      bigChapterName: z.string(),
      description: z.string().nullable(),
      orderingNumber: z.number(),
    }),
  ),
})
export type PublisherCurriculumDifference = z.infer<typeof PublisherCurriculumDifferenceSchema>
